<template>
  <div
    ref="list"
    role="listbox"
    class="overflow-y-auto relative cursor-pointer pr-1 h-full"
    :aria-labelledby="labelledBy"
    :aria-activedescendant="`item-${currentItemIndex}`"
  >
    <div
      class="relative md:text-20 font-bold px-3 md:px-5 py-5 rounded-8 hover:bg-gray-100"
      role="option"
      v-for="(item, index) in filteredItems"
      :key="index"
      :class="{
        'option-border -mt-px': index !== 0 && item[itemValue] !== value,
        'bg-gray-100': index === currentItemIndex && item[itemValue] !== value,
        'bg-amber-500/25': item[itemValue] === value,
      }"
      :aria-selected="item[itemValue] === value"
      @click.stop="$emit('selectItem', item)"
    >
      <slot :item="item" />
    </div>
  </div>
</template>

<script lang="ts" setup generic="T extends Item">
import type { Item } from './types'

const ITEM_SCROLL_OFFSET = 75

defineEmits(['selectItem'])

const props = defineProps<{
  labelledBy?: string
  itemValue: string
  currentItemIndex: number
  filteredItems: T[]
  value: string | null
}>()

const list = useTemplateRef('list')

onMounted(async () => {
  await scrollToCurrentItem()
  list.value?.parentElement?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
})

onUpdated(async () => {
  await scrollToCurrentItem()
})

async function scrollToCurrentItem() {
  await nextTick(() => {
    const currentItem = (
      props.currentItemIndex === -1
        ? list.value?.querySelector('[aria-selected="true"]')
        : list.value?.children[props.currentItemIndex]
    ) as HTMLElement | null | undefined
    if (currentItem && list.value) {
      list.value.scrollTop = currentItem.offsetTop - ITEM_SCROLL_OFFSET
    }
  })
}
</script>

<style scoped>
.option-border:before {
  content: '';
  @apply absolute top-0 inset-x-0 mx-3 md:mx-5 border-b-2 border-gray-100;
}

div::-webkit-scrollbar {
  @apply w-1.5;
}

div::-webkit-scrollbar-thumb {
  @apply bg-gray-500 rounded-20;
}

div::-webkit-scrollbar-thumb:hover {
  @apply bg-gray-600;
}

div::-webkit-scrollbar-track {
  @apply bg-gray-100 bg-clip-content border-solid border-2 border-transparent rounded-20;
}

div::-webkit-scrollbar-corner {
  @apply bg-transparent;
}
</style>
